import React from "react";
import { type MessageDescriptor, useIntl } from "react-intl";

import {
  createTranslator,
  type IntlValues,
  type MessagesMap,
} from "@/app/utils/intlMessage.utils";

/**
 * Creates a locale-aware translator function for a given set of react-intl messages.
 * Delegates key-to-descriptor resolution to {@link createTranslator}, using
 * `intl.formatMessage` as the format function so the active locale is respected.
 *
 * For use outside React (schemas, utils), use {@link createTranslator} directly.
 *
 * @param {T} messages An object of message descriptors, typically from defineMessages.
 * @returns {Function} A translator function that takes a message key and optional values.
 *
 * @example
 * const t = useIntlMessages(authLoginMessages);
 * // ...
 * <p>{t("enterOtpTitle")}</p>
 */
export function useIntlMessages<T extends MessagesMap>(
  messages: T,
): (
  key: keyof T | MessageDescriptor,
  values?: IntlValues | undefined,
) => string {
  const intl = useIntl();

  return React.useCallback(
    (key, values) => {
      const translate = createTranslator(
        messages,
        (descriptor, translateValues) =>
          intl.formatMessage(descriptor, translateValues),
      );

      return translate(key, values);
    },
    [intl, messages],
  );
}
